import { z } from 'zod';
import type { FormField } from './content.ts';

export const formActionRuleSchema = z.discriminatedUnion('operator', [
  z.strictObject({
    fieldName: z.string(),
    operator: z.enum(['Equals', 'NotEquals']),
    value: z.union([z.string(), z.boolean(), z.number()]),
  }),
  z.strictObject({
    fieldName: z.string(),
    operator: z.enum(['Contains', 'NotContains']),
    value: z.string(),
  }),
]);

export const formActionConditionSchema = z.strictObject({
  match: z.enum(['All', 'Any']),
  rules: z.array(formActionRuleSchema).min(1).max(20),
});
export type FormActionRule = z.infer<typeof formActionRuleSchema>;
export type FormActionCondition = z.infer<typeof formActionConditionSchema>;

/** Compare normalized submission values exactly, without coercion or substring matching. */
export function matchesFormActionCondition(
  condition: FormActionCondition | undefined,
  data: Record<string, unknown>,
): boolean {
  if (!condition) return true;
  const matches = (rule: FormActionRule): boolean => {
    if (!Object.hasOwn(data, rule.fieldName)) return false;
    const value = data[rule.fieldName];
    if (rule.operator === 'Contains' || rule.operator === 'NotContains') {
      if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) return false;
      const contains = value.includes(rule.value);
      return rule.operator === 'Contains' ? contains : !contains;
    }
    if (typeof value !== typeof rule.value) return false;
    return rule.operator === 'Equals' ? value === rule.value : value !== rule.value;
  };
  return condition.match === 'All' ? condition.rules.every(matches) : condition.rules.some(matches);
}

export function formActionRuleError(rule: FormActionRule, fields: FormField[]): string | undefined {
  const field = fields.find((field) => field.name === rule.fieldName);
  if (!field) return 'Choose an available form field.';
  const contains = rule.operator === 'Contains' || rule.operator === 'NotContains';
  if (contains !== (field.type === 'CheckboxGroup'))
    return field.type === 'CheckboxGroup'
      ? 'Use contains or does not contain for a multiple choice field.'
      : 'Contains requires a multiple choice field.';
  const valueType = field.type === 'Checkbox' ? 'boolean' : field.type === 'Number' ? 'number' : 'string';
  if (typeof rule.value !== valueType) return `The comparison value must be a ${valueType}.`;
  if (
    (field.type === 'Select' || field.type === 'RadioGroup' || field.type === 'CheckboxGroup') &&
    !field.options.some((option) => option.value === rule.value)
  )
    return 'Choose an available field option.';
}
